import { cn, Icon, Text } from '@mntn/ui-components'
import { useState } from 'react'
import { DealInfo } from '@/components/dashboard/order-item-list/order-item-card/deal-info'
import { AllocationNotify } from '@/components/shared/allocation-notify'
import { AllocationStatusBadge } from '@/components/shared/allocation-status-badge'
import { Badge } from '@/components/shared/badge'
import { CampaignInfo } from '@/components/shared/campaign-info'
import { SpendMeter } from '@/components/shared/spend-meter'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
} from '@/components/shared/types'
import { getAllocationChecks, getOfferingText, hasSpendError } from '@/components/shared/utils'
import { isStartingSoon } from '@/components/shared/utils/is-starting-soon'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { useTriggerAnimation } from '@/hooks/use-trigger-animation'
import { formatNames } from '@/utils/format-names'
import { AirDateInfo } from './air-date-info'
import { CampaignActions } from './campaign-actions'

interface Props extends CreateCampaignCallbacks {
  orderItem: OrderItem
  isExpanded: boolean
  handleSelectOrderItem: () => void
  handleToggleRow: () => void
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  isVisible?: boolean
  animationDelay?: number
  isLoading?: boolean
  isDisabled?: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OrderItemListCard: React.FC<Props> = ({
  orderItem,
  isExpanded,
  handleSelectOrderItem,
  handleToggleRow,
  onCreateCampaignV2,
  onDuplicateCampaign,
  onArchiveCampaign,
  isVisible = true,
  animationDelay = 0,
  isLoading = false,
  isDisabled = false,
}) => {
  const [hasAnimated, setHasAnimated] = useState(false)

  useTriggerAnimation(isVisible && !hasAnimated, setHasAnimated, animationDelay)

  const {
    airStartTime,
    airEndTime,
    offeringName,
    coverMediaUrl,
    campaignGroups,
    programs,
    impressionMetrics: { unallocated: unallocatedImpressions },
    budgetMetrics: {
      unallocated: unallocatedBudget,
      delivered: spendAmount,
      total: totalBudget,
      effective: effectiveBudget,
      ineffective: ineffectiveBudget,
    },
  } = orderItem

  const formattedNumPrograms = getOfferingText(programs.length)
  const formattedProgramNames = formatNames(programs.map(p => p.programName))

  const hasNoCampaigns = campaignGroups.length === 0
  const hasIncompleteCampaigns =
    hasNoCampaigns ||
    campaignGroups.some(campaign => campaign.status === 'INCOMPLETE' || campaign.status === 'ACTION_REQ')

  //TODO: Looks at airEndTime because we're allowing overlapping availability and air dates
  const shouldShowStartingSoonWarning =
    airEndTime !== null &&
    isStartingSoon(new Date(airEndTime)) &&
    (unallocatedImpressions > 0 || hasIncompleteCampaigns)

  const { isFullyAllocated } = getAllocationChecks({
    effectiveBudget,
    ineffectiveBudget,
    unallocatedBudget,
  })

  return (
    <div
      className={cn(
        'space-y-1 transition-all duration-500 ease-out',
        hasAnimated && isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95',
      )}
    >
      <div className="relative shadow-md rounded-sm border border-border bg-surface-bg-inverse">
        <div className="px-6 pt-6 pb-4">
          {shouldShowStartingSoonWarning && (
            <div className="pb-8">
              <div className="flex items-center gap-8 bg-error rounded-xs p-2">
                <div className="flex items-center gap-2">
                  <Icon name="TriangleAlertFilled" color="white" className="size-4" />
                  <Text variant="sm-sb" className="truncate text-header-inverse">
                    Expires Soon
                  </Text>
                </div>
                <Text variant="sm-sb" className="truncate text-header-inverse">
                  Please create a campaign or add inventory to existing campaigns to fill your inventory.
                </Text>
              </div>
            </div>
          )}

          <div className="flex items-center gap-6">
            {/* Thumbnail */}
            <div className="relative shrink-0">
              <DealInfo
                offeringName={offeringName}
                coverMediaUrl={coverMediaUrl}
                onViewDetails={handleSelectOrderItem}
              />
              {orderItem.isTest && (
                <Badge
                  label="Test"
                  type="warning"
                  variant="solid"
                  shape="square"
                  textVariant="base-xss"
                  className="absolute top-2 right-2 z-10 px-2 py-0.5"
                />
              )}
              {isFullyAllocated && (
                <AllocationStatusBadge
                  status="allocated"
                  className="absolute top-2 left-2 z-10 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.25)]"
                />
              )}
            </div>
            {/* Content */}
            <div className="flex flex-col flex-1 min-w-0">
              <AirDateInfo airStartTime={airStartTime} airEndTime={airEndTime} />
              <div className="mb-2 mt-1">
                <Text variant="h4" className="text-header-inverse cursor-pointer">
                  {offeringName}
                </Text>
                <Text variant="sm-sb" className="text-secondary inline">
                  {formattedNumPrograms}:{' '}
                </Text>
                <Text variant="base-sm" className="text-secondary inline">
                  {formattedProgramNames}
                </Text>
              </div>
              <SpendMeter
                spendAmount={spendAmount}
                totalBudget={totalBudget}
                hasSpendError={hasSpendError(orderItem)}
                inverse
              />
            </div>
            {/* Campaign Actions */}
            <div className="shrink-0 self-end pb-1">
              <CampaignActions
                isLoading={isLoading}
                isDisabled={isDisabled}
                onCreateCampaignV2={onCreateCampaignV2}
                handleToggleRow={handleToggleRow}
                orderItem={orderItem}
                isExpanded={isExpanded}
              />
            </div>
          </div>

          <AllocationNotify
            effectiveBudget={effectiveBudget}
            ineffectiveBudget={ineffectiveBudget}
            unallocatedBudget={unallocatedBudget}
            inverse
            layout="inline"
            onSeeDetails={handleSelectOrderItem}
            dismissible
            className="mt-4"
          />

          {campaignGroups.length > 0 && isExpanded && (
            <div className="mt-6 -mx-6">
              <CampaignInfo
                campaigns={campaignGroups}
                variant="dark"
                onDuplicateCampaign={onDuplicateCampaign}
                onArchiveCampaign={onArchiveCampaign}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
